/**
 * Text-to-Speech (TTS) utility
 *
 * Uses the browser's built-in SpeechSynthesis API (free, no API key needed).
 * Falls back gracefully when not supported.
 *
 * Supports Turkish (tr-TR) and English (en-US).
 */

type TTSState = 'idle' | 'speaking' | 'paused';

type TTSListener = (state: TTSState) => void;

class TTSService {
  private synthesis: SpeechSynthesis | null = null;
  private currentUtterance: SpeechSynthesisUtterance | null = null;
  private listeners: Set<TTSListener> = new Set();
  private state: TTSState = 'idle';
  private enabled: boolean = false;
  private preferredVoiceURI: string | null = null;
  private voicesCache: SpeechSynthesisVoice[] = [];

  constructor() {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      this.synthesis = window.speechSynthesis;
      this.loadVoices();
      // Voices may load asynchronously
      this.synthesis.onvoiceschanged = () => this.loadVoices();
    }
  }

  private loadVoices() {
    if (!this.synthesis) return;
    this.voicesCache = this.synthesis.getVoices();
  }

  private setState(state: TTSState) {
    this.state = state;
    this.listeners.forEach((listener) => listener(state));
  }

  subscribe(listener: TTSListener): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  setEnabled(enabled: boolean) {
    this.enabled = enabled;
    if (!enabled) {
      this.stop();
    }
  }

  getState(): TTSState {
    return this.state;
  }

  getVoices(): SpeechSynthesisVoice[] {
    return this.voicesCache;
  }

  /**
   * Pick the best available voice for the given language.
   */
  private pickVoice(lang: string): SpeechSynthesisVoice | null {
    const voices = this.voicesCache.length ? this.voicesCache : (this.synthesis?.getVoices() || []);
    if (!voices.length) return null;

    const langCode = lang === 'tr' ? 'tr' : 'en';
    const langPrefix = langCode === 'tr' ? 'tr-TR' : 'en-US';

    // 1. Preferred voice (if set and matches language)
    if (this.preferredVoiceURI) {
      const preferred = voices.find((v) => v.voiceURI === this.preferredVoiceURI);
      if (preferred && preferred.lang.startsWith(langCode)) {
        return preferred;
      }
    }

    // 2. Exact match (tr-TR / en-US)
    const exact = voices.find((v) => v.lang === langPrefix);
    if (exact) return exact;

    // 3. Language prefix match
    const prefix = voices.find((v) => v.lang.toLowerCase().startsWith(langCode));
    if (prefix) return prefix;

    // 4. Any voice
    return voices[0] || null;
  }

  /**
   * Speak the given text. Stops any current speech.
   */
  speak(text: string, lang: string = 'tr', opts?: { rate?: number; pitch?: number; onEnd?: () => void }) {
    if (!this.synthesis || !this.enabled || !text.trim()) {
      opts?.onEnd?.();
      return;
    }

    // Stop any current speech
    this.stop();

    // Clean text: remove markdown, excessive whitespace
    const cleanText = text
      .replace(/[*_#`>~|-]/g, '')
      .replace(/\n{3,}/g, '\n\n')
      .replace(/\s{2,}/g, ' ')
      .trim();

    if (!cleanText) {
      opts?.onEnd?.();
      return;
    }

    const utterance = new SpeechSynthesisUtterance(cleanText);
    const voice = this.pickVoice(lang);

    if (voice) {
      utterance.voice = voice;
      utterance.lang = voice.lang;
    } else {
      utterance.lang = lang === 'tr' ? 'tr-TR' : 'en-US';
    }

    utterance.rate = opts?.rate ?? 1.0;
    utterance.pitch = opts?.pitch ?? 1.0;
    utterance.volume = 1.0;

    utterance.onstart = () => this.setState('speaking');
    utterance.onend = () => {
      this.setState('idle');
      this.currentUtterance = null;
      opts?.onEnd?.();
    };
    utterance.onerror = (e) => {
      // 'interrupted' and 'canceled' are expected when we stop() mid-speech
      if (e.error !== 'interrupted' && e.error !== 'canceled') {
        console.warn('[TTS] Error:', e.error);
      }
      this.setState('idle');
      this.currentUtterance = null;
      opts?.onEnd?.();
    };

    this.currentUtterance = utterance;

    // Chrome bug: sometimes needs a tick before speaking
    setTimeout(() => {
      if (this.synthesis && this.currentUtterance === utterance) {
        this.synthesis.speak(utterance);
      }
    }, 50);
  }

  /**
   * Stop current speech.
   */
  stop() {
    if (this.synthesis) {
      this.synthesis.cancel();
    }
    this.currentUtterance = null;
    this.setState('idle');
  }

  /**
   * Pause current speech (if supported).
   */
  pause() {
    if (this.synthesis && this.state === 'speaking') {
      this.synthesis.pause();
      this.setState('paused');
    }
  }

  /**
   * Resume paused speech.
   */
  resume() {
    if (this.synthesis && this.state === 'paused') {
      this.synthesis.resume();
      this.setState('speaking');
    }
  }

  /**
   * Check if TTS is supported in this browser.
   */
  isSupported(): boolean {
    return this.synthesis !== null;
  }

  /**
   * Set preferred voice by URI.
   */
  setPreferredVoice(voiceURI: string | null) {
    this.preferredVoiceURI = voiceURI;
  }
}

// Singleton instance
export const tts = new TTSService();
export type { TTSState };
